import AppConfig from '@/config';

export type ProfilePictureEntityType = 'leads' | 'users';

// UUID v4 pattern — used to distinguish real Lead UUIDs from Instagram user IDs
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Constructs the backend URL for a cached profile picture.
 *
 * Instagram CDN URLs expire after a few hours/days, causing broken images.
 * The backend caches profile pictures in S3 and serves them via a public endpoint.
 * - Leads: cached for 14 days, refreshed on manual sync-profile requests
 * - Users (Instagram accounts): cached permanently, refreshed every 7 days
 *
 * For Instagram-sourced leads (non-UUID entityId = Instagram user ID), the original
 * CDN URL and business profile ID are forwarded as query params so the backend
 * can cache the picture without needing a Lead record in DynamoDB.
 *
 * @param entityType - 'leads' or 'users'
 * @param entityId - Lead UUID or Instagram user ID
 * @param originalUrl - The original profile picture URL (used to determine if a picture exists)
 * @param businessProfileId - Connected IG account user ID (Instagram-sourced leads only)
 * @returns The backend profile picture URL, or undefined if no picture exists
 */
export function getProfilePictureUrl(
  entityType: ProfilePictureEntityType,
  entityId: string,
  originalUrl?: string | null,
  businessProfileId?: string | null
): string | undefined {
  if (!originalUrl || !entityId) return undefined;

  const base = `${AppConfig.endpoints.client.base}/public/profile-picture/${entityType}/${entityId}`;

  // For Instagram-sourced leads (non-UUID), pass the CDN URL and business profile ID
  // so the backend can cache the picture without a Lead record in DynamoDB.
  const isInstagramLead = entityType === 'leads' && !UUID_RE.test(entityId);
  if (isInstagramLead && (originalUrl || businessProfileId)) {
    const params = new URLSearchParams();
    if (originalUrl) params.set('original_url', originalUrl);
    if (businessProfileId) params.set('business_profile_id', businessProfileId);
    return `${base}?${params.toString()}`;
  }

  return base;
}

/**
 * Constructs the backend URL for a cached story media thumbnail.
 *
 * Instagram story CDN URLs expire after hours/days, causing broken thumbnails.
 * The backend caches story media in S3 and serves it via a public endpoint.
 */
export function getStoryMediaUrl(leadId: string): string {
  return `${AppConfig.endpoints.client.base}/public/story-media/${leadId}`;
}
